import { Cache, LinearEncoding, TextureLoader } from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader';
import { appMixin } from '../utils/appMixin';
import { computeEnvmap } from '../utils/misc';

Cache.enabled = true;

export class Loader extends appMixin() {
  constructor(app) {
    super(app);
    this._assetsToLoad = [];
    this._assetsSizeKnown = 0;
    this.progress = 0;

    this._textureLoader = new TextureLoader();
    this._gltfLoader = new GLTFLoader();
    this._rgbeLoader = new RGBELoader();

    /** @type {Map<string, import('three').Texture>} */
    this.loadedTextures = new Map();
    /** @type {Map<string, import('three').Object3D>} */
    this.loadedModels = new Map();
    /** @type {Map<string, Object>} */
    this.loadedJSONs = new Map();
    /** @type {Map<string, string>} */
    this.loadedRaws = new Map();
    /** @type {Map<string, string>} */
    this.loadedFonts = new Map();
  }

  add(...assets) {
    this._assetsToLoad.push({ ...assets, progress: 0, size: 0 });
  }

  getTexture(key) {
    if (this.loadedTextures.has(key)) return this.loadedTextures.get(key);
    /// #if DEBUG
    console.error(`Loaded texture ${key} not found`);
    /// #endif
  }

  getModel(key) {
    if (this.loadedModels.has(key)) return this.loadedModels.get(key);
    /// #if DEBUG
    console.error(`Loaded model ${key} not found`);
    /// #endif
  }

  getJSON(key) {
    if (this.loadedJSONs.has(key)) return this.loadedJSONs.get(key);
    /// #if DEBUG
    console.error(`Loaded JSON ${key} not found`);
    /// #endif
  }

  getRaw(key) {
    if (this.loadedRaws.has(key)) return this.loadedRaws.get(key);
    /// #if DEBUG
    console.error(`Loaded raw ${key} not found`);
    /// #endif
  }

  getFont(key) {
    if (this.loadedFonts.has(key)) return this.loadedFonts.get(key);
    /// #if DEBUG
    console.error(`Loaded font ${key} not found`);
    /// #endif
  }

  loadAssets(assets) {
    // this._assetsToLoad.sort((a, b) => a.priority - b.priority);
    return Promise.all(assets.map((asset) => this.loadAsset(asset.key, asset.path, asset.type)));
  }

  loadAsset(key, path, type = '') {
    let assetPromise;
    let ext = path.split('.').pop().toLowerCase();
    if (type) ext = type;
    switch (ext) {
      case 'jpg':
      case 'jpeg':
      case 'png':
        assetPromise = this.loadTexture(key, path);
        break;
      case 'hdr':
        assetPromise = this.loadEnvMap(key, path);
        break;
      case 'gltf':
      case 'glb':
        assetPromise = this.loadModel(key, path);
        break;
      case 'fnt':
      case 'json':
        assetPromise = this.loadJSON(key, path);
        break;
      case 'woff':
      case 'woff2':
      case 'otf':
      case 'ttf':
        assetPromise = this.loadFont(key, path);
        break;
      default:
        assetPromise = this.loadRaw(key, path);
        break;
    }
    return assetPromise;
  }

  /**
   *
   * @param {string} key
   * @returns {Promise<import('three').Texture | null>}
   */
  async loadTexture(key, path, force = false) {
    let loadedTexture = this.loadedTextures.get(key);
    if (!loadedTexture || force) {
      loadedTexture = await this._textureLoader.loadAsync(path, (e) => this.assetProgress(e, key));
      this.loadedTextures.set(key, loadedTexture);
    }
    return loadedTexture;
  }

  /**
   *
   * @param {string} key
   * @returns {Promise<import('three').Texture | null>}
   */
  async loadEnvMap(key, path, force = false) {
    let loadedTexture = this.loadedTextures.get(key);
    if (!loadedTexture || force) {
      loadedTexture = await this._rgbeLoader.loadAsync(path, (e) => this.assetProgress(e, key));
      this.loadedTextures.set(key, loadedTexture);
    }

    return computeEnvmap(this.app.webgl.renderer, loadedTexture);
  }

  /**
   *
   * @param {string} key
   * @returns {Promise<import('three').Object3D | null>}
   */
  async loadModel(key, path, force = false) {
    let loadedModel = this.loadedModels.get(key);
    if (!loadedModel || force) {
      const gltf = await this._gltfLoader.loadAsync(path, (e) => this.assetProgress(e, key));
      gltf.scene.traverse((object) => {
        if (object.isMesh && object.material?.map) {
          object.material.map.encoding = LinearEncoding;
          object.material.envMapIntensity = 0.5;
        }
      });
      const { animations } = gltf;
      loadedModel = gltf.scene;
      loadedModel.animations = animations;
      this.loadedModels.set(key, loadedModel);
    }
    return loadedModel;
  }

  async loadRaw(key, path, force = false) {
    let loadedRaw = this.loadedRaws.get(key);

    if (!loadedRaw || force) {
      const response = await fetch(path);
      loadedRaw = await response.text();
      this.loadedRaws.set(key, loadedRaw);
    }

    return loadedRaw;
  }

  async loadJSON(key, path, force = false) {
    let loadedJSON = this.loadedJSONs.get(key);
    if (!loadedJSON || force) {
      const response = await fetch(path);
      loadedJSON = await response.json();
      this.loadedJSONs.set(key, loadedJSON);
    }
    return loadedJSON;
  }

  async loadFont(key, path, force = false) {
    let loadedFont = this.loadedFonts.get(key);
    if (!loadedFont || force) {
      const loadedFont = new FontFace(key, `url(${path})`);
      await new Promise((resolve) => {
        loadedFont.load().then(() => {
          resolve();
        });
      });
      this.loadedFonts.set(key, loadedFont);
    }
    return loadedFont;
  }

  assetProgress() {
    // TODO fix this
    /**
      const asset = this._assetsToLoad.find((a) => a.key === key);

      if (!asset.size) {
        asset.size = e.total;
        this._assetsSizeKnown++;
      }
      asset.progress = e.loaded / asset.size;

      this.progress = this._assetsToLoad.map((asset) => asset.progress).reduce((previousValue, currentValue) => previousValue + currentValue, 0) / this._assetsSizeKnown;
      state.emit(EVENTS.LOADER_PROGRESS, this.progress);
     */
  }
}
